import { useState } from "react";
import { usePipelineConfig, type StageConfig } from "@/data/PipelineConfigContext";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, GripVertical, Pencil, Archive, ArchiveRestore, AlertTriangle, Settings2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export function PipelineStageManager() {
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const { activePipeline, addStage, renameStage, archiveStage, restoreStage } = usePipelineConfig();
  const { leads } = useAppData();
  const [open, setOpen] = useState(false);
  const [newStageName, setNewStageName] = useState("");
  const [newStageType, setNewStageType] = useState<StageConfig["type"]>("active");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const isAdmin = hasPerm("jobs_edit");
  if (!isAdmin) return null;

  const visibleStages = activePipeline.stages.filter((s) => !s.archived);
  const archivedStages = activePipeline.stages.filter((s) => s.archived);

  function getLeadCount(stageName: string) {
    return leads.filter((l) => l.stage === stageName).length;
  }

  function handleAddStage() {
    if (!newStageName.trim()) return;
    if (activePipeline.stages.some((s) => s.name.toLowerCase() === newStageName.trim().toLowerCase())) {
      toast.error("Stage name already exists");
      return;
    }
    addStage(newStageName.trim(), newStageType);
    setNewStageName("");
    toast.success(`Stage "${newStageName.trim()}" added`);
  }

  function handleRename(stageId: string) {
    if (!editName.trim()) return;
    renameStage(stageId, editName.trim());
    setEditingId(null);
    toast.success("Stage renamed");
  }

  function handleArchive(stage: StageConfig) {
    const count = getLeadCount(stage.name);
    if (count > 0) {
      toast.error(`Cannot archive "${stage.name}" — ${count} active lead(s). Reassign them first.`);
      return;
    }
    archiveStage(stage.id);
    toast.success(`Stage "${stage.name}" archived`);
  }

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="gap-1.5">
        <Settings2 className="h-3.5 w-3.5" /> Stages
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Pipeline Stages</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Active stages */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Active Stages</p>
              <div className="space-y-1.5">
                {visibleStages.map((stage) => {
                  const count = getLeadCount(stage.name);
                  const isEditing = editingId === stage.id;
                  return (
                    <div key={stage.id} className="flex items-center gap-2 group">
                      <GripVertical className="h-3.5 w-3.5 text-muted-foreground/30 shrink-0" />
                      <Badge variant="outline" className={cn("text-[10px] shrink-0", stage.color)}>
                        {stage.type === "won" ? "Won" : stage.type === "lost" ? "Lost" : "Active"}
                      </Badge>
                      {isEditing ? (
                        <div className="flex-1 flex gap-1">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="h-7 text-xs"
                            autoFocus
                            onKeyDown={(e) => { if (e.key === "Enter") handleRename(stage.id); if (e.key === "Escape") setEditingId(null); }}
                          />
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => handleRename(stage.id)}>Save</Button>
                        </div>
                      ) : (
                        <>
                          <span className="text-sm flex-1">{stage.name}</span>
                          {count > 0 && <span className="text-[10px] text-muted-foreground">{count} leads</span>}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100"
                            onClick={() => { setEditingId(stage.id); setEditName(stage.name); }}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100"
                            onClick={() => handleArchive(stage)}
                          >
                            <Archive className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Add new stage */}
            <div className="border-t pt-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Add New Stage</p>
              <div className="flex gap-2">
                <Input
                  value={newStageName}
                  onChange={(e) => setNewStageName(e.target.value)}
                  placeholder="Stage name..."
                  className="h-8 flex-1 text-sm"
                  onKeyDown={(e) => { if (e.key === "Enter") handleAddStage(); }}
                />
                <Select value={newStageType} onValueChange={(v) => setNewStageType(v as StageConfig["type"])}>
                  <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-8" onClick={handleAddStage} disabled={!newStageName.trim()}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Archived stages */}
            {archivedStages.length > 0 && (
              <div className="border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground mb-2">Archived Stages</p>
                <div className="space-y-1.5">
                  {archivedStages.map((stage) => (
                    <div key={stage.id} className="flex items-center gap-2 opacity-60">
                      <span className="text-sm flex-1 line-through">{stage.name}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-xs gap-1"
                        onClick={() => { restoreStage(stage.id); toast.success(`Stage "${stage.name}" restored`); }}
                      >
                        <ArchiveRestore className="h-3 w-3" /> Restore
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-md">
              <AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5 shrink-0" />
              <p className="text-[11px] text-muted-foreground">
                Stages with active leads cannot be archived. Reassign leads before archiving.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
